/**
 * Pointer-Events swipe + tap detector. Designed for iPhone Safari:
 *   - Suppresses Safari swipe-back / pull-to-refresh / pinch-zoom on the canvas.
 *   - Uses { passive: false } on pointerdown so preventDefault() actually wins.
 *   - Only emits one of: swipe-left/right/up/down OR tap, never both.
 */
export type SwipeDir = "left" | "right" | "up" | "down";

export interface InputEvents {
  onSwipe?: (dir: SwipeDir) => void;
  onTap?: (x: number, y: number) => void;
  onFirstInteract?: () => void;
}

const SWIPE_MIN_DIST = 22; // px — slightly tighter than before for snappier feel
const SWIPE_MAX_TIME = 380; // ms (only enforced for tap-vs-swipe disambiguation)
const TAP_MAX_DIST = 10;
const TAP_MAX_TIME = 220;

export class Input {
  private startX = 0;
  private startY = 0;
  private startT = 0;
  private active = false;
  private consumed = false; // swipe already fired this gesture
  private firedFirst = false;
  private events: InputEvents;
  private el: HTMLElement;
  private bound = {
    down: this.onDown.bind(this),
    move: this.onMove.bind(this),
    up: this.onUp.bind(this),
    cancel: this.onCancel.bind(this),
    keydown: this.onKey.bind(this),
  };

  constructor(target: HTMLElement, events: InputEvents) {
    this.el = target;
    this.events = events;
  }

  attach(): void {
    this.el.addEventListener("pointerdown", this.bound.down, { passive: false });
    this.el.addEventListener("pointermove", this.bound.move, { passive: false });
    this.el.addEventListener("pointerup", this.bound.up, { passive: false });
    this.el.addEventListener("pointercancel", this.bound.cancel, { passive: false });
    window.addEventListener("keydown", this.bound.keydown);
  }

  detach(): void {
    this.el.removeEventListener("pointerdown", this.bound.down);
    this.el.removeEventListener("pointermove", this.bound.move);
    this.el.removeEventListener("pointerup", this.bound.up);
    this.el.removeEventListener("pointercancel", this.bound.cancel);
    window.removeEventListener("keydown", this.bound.keydown);
  }

  private fireFirst(): void {
    if (this.firedFirst) return;
    this.firedFirst = true;
    this.events.onFirstInteract?.();
  }

  private onDown(e: PointerEvent): void {
    e.preventDefault();
    this.fireFirst();
    this.active = true;
    this.consumed = false;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startT = performance.now();
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* not all browsers */
    }
  }

  /**
   * Fire swipes as soon as the finger crosses the distance threshold,
   * rather than waiting for pointerup. Removes the perceived lag between
   * the player flicking sideways and the car actually changing lanes.
   * Once a swipe has fired, the gesture is "consumed" and won't emit
   * another swipe or a tap on lift.
   */
  private onMove(e: PointerEvent): void {
    if (!this.active) return;
    e.preventDefault();
    if (this.consumed) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const dist = Math.hypot(dx, dy);
    if (dist < SWIPE_MIN_DIST) return;
    this.consumed = true;
    if (Math.abs(dx) > Math.abs(dy)) {
      this.events.onSwipe?.(dx > 0 ? "right" : "left");
    } else {
      this.events.onSwipe?.(dy > 0 ? "down" : "up");
    }
  }

  private onUp(e: PointerEvent): void {
    if (!this.active) return;
    e.preventDefault();
    this.active = false;
    if (this.consumed) return; // swipe already fired during pointermove
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const dt = performance.now() - this.startT;
    const dist = Math.hypot(dx, dy);

    if (dist < TAP_MAX_DIST && dt < TAP_MAX_TIME) {
      this.events.onTap?.(e.clientX, e.clientY);
      return;
    }
    // Late, slow swipe that never crossed the threshold while moving but
    // ends up far from the start point — still treat it as a swipe.
    if (dist < SWIPE_MIN_DIST || dt > SWIPE_MAX_TIME) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      this.events.onSwipe?.(dx > 0 ? "right" : "left");
    } else {
      this.events.onSwipe?.(dy > 0 ? "down" : "up");
    }
  }

  private onCancel(_e: PointerEvent): void {
    this.active = false;
    this.consumed = false;
  }

  // Desktop fallback so we can develop without a touchscreen.
  private onKey(e: KeyboardEvent): void {
    this.fireFirst();
    switch (e.key) {
      case "ArrowLeft":
      case "a":
      case "A":
        this.events.onSwipe?.("left");
        break;
      case "ArrowRight":
      case "d":
      case "D":
        this.events.onSwipe?.("right");
        break;
      case "ArrowUp":
      case "w":
      case "W":
      case " ":
        this.events.onSwipe?.("up");
        break;
      case "ArrowDown":
      case "s":
      case "S":
        this.events.onSwipe?.("down");
        break;
      case "Enter":
        this.events.onTap?.(0, 0);
        break;
    }
  }
}
